import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Building2,
  Brain,
  TrendingUp,
  CreditCard,
  Lock,
  Zap,
  Globe2,
  CheckCircle2,
  Users,
  FileCheck,
  ChevronRight,
  Send,
  Wallet,
  LineChart,
  Layers,
  Bot,
  MapPin,
  Mail,
  Phone,
} from "lucide-react";
import FinnyChat from "../components/FinnyChat";

gsap.registerPlugin(ScrollTrigger);

const FINANCIFY_LOGO =
  "https://customer-assets.emergentagent.com/job_cf39cd38-3e4c-4cdf-8fd4-859e0591d26f/artifacts/0ftkiwrk_IMG_2860.png";

const JOURNEY_STEPS = [
  {
    n: "01",
    title: "Enter your income & debts",
    desc: "Share your employment, salary, and existing obligations through a guided 7-step form.",
    icon: Wallet,
    accent: "#52BFB8",
  },
  {
    n: "02",
    title: "AI calculates affordability & TDSR",
    desc: "Our engine instantly computes your Total Debt-Service Ratio against Caribbean lender thresholds.",
    icon: Brain,
    accent: "#60a5fa",
  },
  {
    n: "03",
    title: "Match with suitable lenders",
    desc: "See which banks, credit unions, and dealers you pre-qualify with — ranked by amount, rate, or term.",
    icon: Layers,
    accent: "#a78bfa",
  },
  {
    n: "04",
    title: "Upload your ID for verification",
    desc: "Snap a photo of your NIB Card, Passport, or Driver's License. Our scanner extracts and verifies your details instantly.",
    icon: ShieldCheck,
    accent: "#fbbf24",
  },
  {
    n: "05",
    title: "Submit to the right institution",
    desc: "Forward your verified profile straight to the lender you choose. They take it from there.",
    icon: Send,
    accent: "#f472b6",
  },
];

const TRUST_BADGES = [
  { icon: Brain, label: "AI-Powered" },
  { icon: ShieldCheck, label: "Secure Verification" },
  { icon: Globe2, label: "Caribbean-Focused" },
  { icon: Zap, label: "Fast Qualification" },
  { icon: Building2, label: "Multiple Lenders" },
];

const PARTNER_FEATURES = [
  {
    icon: Layers,
    title: "White-Label Loan Application",
    desc: "Embed Financify's qualification engine on your domain with your branding.",
  },
  {
    icon: TrendingUp,
    title: "Smart Lead Routing",
    desc: "Verified, pre-qualified applicants delivered to the right desk in real-time.",
  },
  {
    icon: ShieldCheck,
    title: "KYC / AML Screening",
    desc: "Sumsub-powered ID and liveness checks compliant with Caribbean regulations.",
  },
  {
    icon: LineChart,
    title: "SaaS Dashboard",
    desc: "Track conversions, lender mix, and applicant quality from one control room.",
  },
];

export default function LandingPage() {
  const heroRef = useRef(null);
  const journeyRef = useRef(null);
  const dashboardRef = useRef(null);
  const finnyRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1024 : false
  );

  // Track viewport
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Apply dark page mode to body
  useEffect(() => {
    document.body.setAttribute("data-dark", "true");
    return () => document.body.removeAttribute("data-dark");
  }, []);

  // Hero entrance + parallax
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from("[data-hero-anim]", {
        y: 40,
        opacity: 0,
        duration: 1.2,
        ease: "power3.out",
        stagger: 0.15,
        delay: 0.2,
      });

      // Floating cards parallax on mousemove
      const cards = gsap.utils.toArray("[data-float-card]");
      const onMove = (e) => {
        const x = (e.clientX / window.innerWidth - 0.5) * 30;
        const y = (e.clientY / window.innerHeight - 0.5) * 20;
        cards.forEach((c, i) => {
          const depth = (i + 1) * 0.4;
          gsap.to(c, { x: x * depth, y: y * depth, duration: 1.2, ease: "power2.out" });
        });
      };
      window.addEventListener("mousemove", onMove);
      return () => window.removeEventListener("mousemove", onMove);
    }, heroRef);
    return () => ctx.revert();
  }, []);

  // Cinematic scroll journey — pin and scrub through steps (desktop only)
  useEffect(() => {
    if (!journeyRef.current) return;
    if (!isDesktop) return; // skip pin on mobile/tablet
    const ctx = gsap.context(() => {
      const total = JOURNEY_STEPS.length;
      const st = ScrollTrigger.create({
        trigger: journeyRef.current,
        start: "top top",
        end: () => `+=${total * window.innerHeight * 0.8}`,
        pin: true,
        scrub: 0.6,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const idx = Math.min(total - 1, Math.floor(self.progress * total));
          setActiveStep(idx);
        },
      });
      // Force a refresh after fonts/images settle
      setTimeout(() => ScrollTrigger.refresh(), 400);
      return () => st.kill();
    }, journeyRef);
    return () => ctx.revert();
  }, [isDesktop]);

  // Mobile journey — individual step scroll-triggered fade-in + scale
  useEffect(() => {
    if (!journeyRef.current) return;
    if (isDesktop) return;
    const ctx = gsap.context(() => {
      gsap.utils.toArray("[data-mobile-step]").forEach((el) => {
        gsap.fromTo(
          el,
          { y: 60, opacity: 0, scale: 0.92 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: {
              trigger: el,
              start: "top 85%",
              end: "top 50%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });
    }, journeyRef);
    return () => ctx.revert();
  }, [isDesktop]);

  // Dashboard reveal
  useEffect(() => {
    if (!dashboardRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-dashboard-card]",
        { y: 60, opacity: 0, rotateX: 12 },
        {
          y: 0,
          opacity: 1,
          rotateX: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: { trigger: dashboardRef.current, start: "top 80%" },
        }
      );
      gsap.fromTo(
        "[data-dashboard-row]",
        { x: -20, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: { trigger: dashboardRef.current, start: "top 70%" },
        }
      );
    }, dashboardRef);
    return () => ctx.revert();
  }, []);

  // Generic fade-in for feature/partner sections
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray("[data-fade-up]").forEach((el) => {
        gsap.fromTo(
          el,
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 85%" },
          }
        );
      });
    });
    return () => ctx.revert();
  }, []);

  return (
    <div className="bg-fin-base text-white font-manrope min-h-screen overflow-x-hidden">
      {/* ───────────── HERO ───────────── */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center pt-28 sm:pt-32 pb-28 sm:pb-20 px-5 lg:px-10"
      >
        {/* Aurora glow blobs */}
        <div className="aurora aurora-anim w-[600px] h-[600px] -top-40 -left-40 bg-[#52BFB8]/30" />
        <div className="aurora aurora-anim w-[500px] h-[500px] top-1/4 right-0 bg-[#3E9690]/25" style={{ animationDelay: "3s" }} />
        <div className="aurora aurora-anim w-[400px] h-[400px] bottom-0 left-1/3 bg-[#60a5fa]/15" style={{ animationDelay: "6s" }} />

        {/* Caribbean grid */}
        <div className="absolute inset-0 caribbean-grid opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#040914]" />

        <div className="relative z-10 max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div data-hero-anim className="flex flex-wrap items-center gap-2 mb-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#52BFB8]/40 bg-[#52BFB8]/10 backdrop-blur-md px-4 py-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#52BFB8] animate-pulse" />
                <span className="text-xs tracking-[0.2em] uppercase text-[#52BFB8] font-semibold">
                  AI Pre-Qualification · Caribbean
                </span>
              </div>
              <div
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 backdrop-blur-md px-3 py-1.5"
                data-testid="beta-badge"
                aria-label="Beta release"
                title="Financify is in public beta — your feedback shapes what we build next."
              >
                <span className="text-[10px] tracking-[0.2em] uppercase text-amber-300 font-bold">
                  Beta
                </span>
              </div>
            </div>

            <h1
              data-hero-anim
              className="font-cabinet text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.05] text-white"
            >
              Multiple Lenders.
              <br />
              <span className="bg-gradient-to-r from-[#52BFB8] via-[#6BD2CB] to-[#3E9690] bg-clip-text text-transparent">
                One AI Qualification
              </span>
              <br />
              Platform.
            </h1>

            <p
              data-hero-anim
              className="mt-7 text-lg md:text-xl text-[#9BA8C4] max-w-xl leading-relaxed"
            >
              Get pre-qualified for loans, mortgages, credit cards, and financing options from the comfort of your home.
            </p>

            <div data-hero-anim className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/qualify" data-testid="hero-cta-prequalify" className="btn-aqua group">
                Start Pre-Qualification
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/partners/verify" data-testid="hero-cta-partners" className="btn-ghost-aqua group">
                Explore Partner Solutions
                <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            <div data-hero-anim className="mt-10 flex items-center gap-6 text-sm text-[#667699]">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#52BFB8]" />
                Bank-grade security
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#52BFB8]" />
                Results in minutes
              </div>
              <div className="hidden md:flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#52BFB8]" />
                5+ Caribbean banks
              </div>
            </div>
          </div>

          {/* Hero floating cards mockup */}
          <div data-hero-anim className="lg:col-span-5 relative h-[500px] hidden lg:block">
            <div
              data-float-card
              className="absolute top-10 right-0 w-72 glass-card rounded-2xl p-5"
              style={{ transform: "rotate(3deg)" }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#52BFB8] to-[#3E9690] flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-[#9BA8C4]">Pre-qualified for</p>
                  <p className="font-semibold text-white">$72,500</p>
                </div>
              </div>
              <div className="space-y-2 text-xs text-[#9BA8C4]">
                <div className="flex justify-between"><span>Monthly</span><span className="text-white font-mono">$842</span></div>
                <div className="flex justify-between"><span>Term</span><span className="text-white">12 yr</span></div>
                <div className="flex justify-between"><span>Rate</span><span className="text-[#52BFB8]">14.5%</span></div>
              </div>
            </div>

            <div
              data-float-card
              className="absolute top-44 right-32 w-64 glass-card rounded-2xl p-5"
              style={{ transform: "rotate(-4deg)" }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-[#9BA8C4] uppercase tracking-wider">TDSR</span>
                <span className="text-xs text-emerald-400">Healthy</span>
              </div>
              <p className="text-3xl font-bold text-white font-cabinet">38.2<span className="text-[#52BFB8]">%</span></p>
              <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full w-[38%] bg-gradient-to-r from-[#52BFB8] to-emerald-400" />
              </div>
            </div>

            <div
              data-float-card
              className="absolute bottom-10 right-12 w-72 glass-card rounded-2xl p-5"
              style={{ transform: "rotate(2deg)" }}
            >
              <div className="flex items-center gap-3 mb-3">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                <p className="font-semibold text-white">ID Verified</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs text-[#9BA8C4]">
                <div className="rounded-lg border border-white/10 py-2"><p className="text-white font-semibold">ID</p><p>Match</p></div>
                <div className="rounded-lg border border-white/10 py-2"><p className="text-white font-semibold">Doc</p><p>Clear</p></div>
                <div className="rounded-lg border border-white/10 py-2"><p className="text-white font-semibold">AML</p><p>Pass</p></div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator (desktop only) */}
        <div className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 text-[#667699] text-xs uppercase tracking-[0.3em] flex-col items-center gap-2">
          <span>Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-[#52BFB8] to-transparent" />
        </div>
      </section>

      {/* ───────────── CINEMATIC SCROLL JOURNEY ───────────── */}
      <section
        ref={journeyRef}
        className="relative overflow-hidden bg-[#040914] py-20 lg:min-h-screen"
        data-testid="journey-section"
      >
        <div className="aurora w-[800px] h-[800px] top-1/2 -translate-y-1/2 -right-60 bg-[#52BFB8]/10" />
        <div className="absolute inset-0 caribbean-grid opacity-40" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10 lg:h-screen flex flex-col justify-center">
          <div className="text-center mb-10 lg:mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-[#52BFB8] font-semibold mb-3">
              THE FINANCIFY JOURNEY
            </p>
            <h2 className="font-cabinet text-3xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
              Five steps to <span className="text-[#52BFB8]">funding clarity</span>.
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center lg:flex-1">
            {/* Left — animated step list */}
            <div className="space-y-3">
              {JOURNEY_STEPS.map((step, i) => {
                const Icon = step.icon;
                // On mobile (no pin), all steps are active for readability
                const active = !isDesktop ? true : i === activeStep;
                return (
                  <div
                    key={step.n}
                    data-mobile-step
                    className={`flex items-start gap-4 sm:gap-5 p-4 sm:p-5 rounded-2xl border transition-all duration-500 ${
                      active
                        ? "border-[#52BFB8]/40 bg-gradient-to-r from-[#52BFB8]/10 to-transparent lg:scale-[1.02]"
                        : "border-white/5 opacity-50"
                    }`}
                    data-testid={`journey-step-${i + 1}`}
                  >
                    <div
                      className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
                        active ? "lg:scale-110" : "scale-100"
                      }`}
                      style={{
                        background: active
                          ? `linear-gradient(135deg, ${step.accent}, ${step.accent}99)`
                          : "rgba(255,255,255,0.05)",
                        boxShadow: active ? `0 0 28px ${step.accent}66` : "none",
                      }}
                    >
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[10px] sm:text-xs font-mono mb-1 ${active ? "text-[#52BFB8]" : "text-[#667699]"}`}>
                        STEP {step.n}
                      </p>
                      <h3 className={`font-cabinet text-lg sm:text-xl md:text-2xl font-semibold mb-1 ${active ? "text-white" : "text-[#9BA8C4]"}`}>
                        {step.title}
                      </h3>
                      <p className={`text-xs sm:text-sm mt-1 sm:mt-2 ${active ? "text-[#9BA8C4]" : "text-[#667699]"} lg:${active ? "block" : "hidden"}`}>
                        {step.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right — animated mockup */}
            <div className="relative h-[500px] hidden lg:block">
              <JourneyVisual step={activeStep} />
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── FINNY MASCOT SECTION ───────────── */}
      <section ref={finnyRef} className="relative py-32 px-5 lg:px-10 overflow-hidden">
        <div className="aurora w-[600px] h-[600px] top-0 left-1/2 -translate-x-1/2 bg-[#52BFB8]/15" />

        <div className="relative max-w-5xl mx-auto text-center" data-fade-up>
          {/* Finny mascot */}
          <div className="relative w-40 h-40 mx-auto mb-10 finny-float">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#52BFB8] via-[#3E9690] to-[#52BFB8] blur-2xl opacity-60" />
            <div className="relative w-full h-full rounded-full bg-gradient-to-br from-[#52BFB8] to-[#3E9690] flex items-center justify-center shadow-[0_0_60px_rgba(82,191,184,0.6)] border-2 border-white/20">
              <Bot className="w-20 h-20 text-white" strokeWidth={1.5} />
              <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-emerald-400 ring-4 ring-[#040914] flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-[#040914]" />
              </span>
            </div>
          </div>

          <p className="text-xs tracking-[0.3em] uppercase text-[#52BFB8] font-semibold mb-3">
            MEET FINNY
          </p>
          <h2 className="font-cabinet text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight max-w-3xl mx-auto">
            Your AI pre-qualification companion for the Caribbean.
          </h2>
          <p className="mt-6 text-lg text-[#9BA8C4] max-w-2xl mx-auto">
            Ask Finny about TDSR, lenders, KYC, or how to get started. He'll point you straight to the form when you're ready.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/qualify" data-testid="finny-section-cta" className="btn-aqua group">
              Start Now
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
            <p className="text-sm text-[#667699] flex items-center gap-2">
              <MessageCircleIcon /> Or tap "Ask Finny" at the bottom right
            </p>
          </div>
        </div>
      </section>

      {/* ───────────── HOW FINANCIFY WORKS — 3 CARDS ───────────── */}
      <section className="relative py-24 px-5 lg:px-10 bg-[#0B1324]/40">
        <div className="absolute inset-0 caribbean-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16" data-fade-up>
            <p className="text-xs tracking-[0.3em] uppercase text-[#52BFB8] font-semibold mb-3">
              HOW FINANCIFY WORKS
            </p>
            <h2 className="font-cabinet text-4xl md:text-5xl font-bold text-white tracking-tight">
              Built for trust. Engineered for speed.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Brain,
                title: "AI Pre-Qualification",
                desc: "Our engine evaluates your TDSR, employment, and debt profile against Bahamian lender rules — instantly.",
              },
              {
                icon: Layers,
                title: "Multiple Lender Matching",
                desc: "See every Caribbean institution you qualify with, ranked by amount, rate, term, or approval likelihood.",
              },
              {
                icon: ShieldCheck,
                title: "Secure KYC & Compliance",
                desc: "Sumsub-powered identity verification, AML screening, and bank-grade encryption from end to end.",
              },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  data-fade-up
                  className="glass-card glass-card-hover rounded-3xl p-8 group transition-all duration-500"
                  data-testid={`how-card-${i}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#52BFB8] to-[#3E9690] flex items-center justify-center mb-6 shadow-[0_0_24px_rgba(82,191,184,0.4)]">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-cabinet text-2xl font-semibold text-white mb-3">{c.title}</h3>
                  <p className="text-[#9BA8C4] leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────────── DASHBOARD PREVIEW ───────────── */}
      <section ref={dashboardRef} className="relative py-32 px-5 lg:px-10 overflow-hidden">
        <div className="aurora w-[700px] h-[700px] top-0 right-0 bg-[#3E9690]/15" />

        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5" data-fade-up>
            <p className="text-xs tracking-[0.3em] uppercase text-[#52BFB8] font-semibold mb-3">
              YOUR DASHBOARD
            </p>
            <h2 className="font-cabinet text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
              Every lender match.<br />One control room.
            </h2>
            <p className="text-lg text-[#9BA8C4] mb-8 leading-relaxed">
              Watch your pre-qualification light up across multiple banks at once. Compare offers, monitor TDSR, and track KYC status — all from a single, beautifully calm dashboard.
            </p>
            <ul className="space-y-3 text-[#9BA8C4]">
              {["Real-time lender match grid", "TDSR & affordability tracking", "Live ID verification status", "One-click submit to chosen lender"].map(
                (item, i) => (
                  <li key={i} className="flex items-center gap-3" data-dashboard-row>
                    <CheckCircle2 className="w-5 h-5 text-[#52BFB8] flex-shrink-0" />
                    {item}
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div
              data-dashboard-card
              className="glass-card rounded-3xl p-6 md:p-8"
              style={{ perspective: "1200px" }}
              data-testid="dashboard-preview"
            >
              {/* Mock toolbar */}
              <div className="flex items-center justify-between pb-4 border-b border-white/5 mb-6">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-rose-400/70" />
                  <span className="w-3 h-3 rounded-full bg-amber-400/70" />
                  <span className="w-3 h-3 rounded-full bg-emerald-400/70" />
                </div>
                <p className="text-xs text-[#667699] font-mono">financify.app/dashboard</p>
                <div className="w-12" />
              </div>

              {/* Top stats */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5">
                {[
                  { label: "Qualified", value: "$72.5K", color: "#52BFB8" },
                  { label: "TDSR", value: "38.2%", color: "#60a5fa" },
                  { label: "Monthly", value: "$842", color: "#a78bfa" },
                ].map((s, i) => (
                  <div key={i} className="rounded-xl bg-white/5 border border-white/5 p-3 sm:p-4 text-center sm:text-left" data-dashboard-row>
                    <p className="text-[10px] sm:text-xs text-[#667699] uppercase tracking-wider mb-1 whitespace-nowrap">{s.label}</p>
                    <p className="font-cabinet text-lg sm:text-2xl font-bold tabular-nums" style={{ color: s.color }}>
                      {s.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Lender match grid */}
              <p className="text-xs uppercase tracking-wider text-[#667699] mb-3">Lender Matches</p>
              <div className="space-y-2 mb-5">
                {[
                  { abbr: "FB", amount: "$72,500", rate: "14.5%", status: "Match", color: "#52BFB8" },
                  { abbr: "CB", amount: "$70,000", rate: "12.0%", status: "Match", color: "#52BFB8" },
                  { abbr: "RBC", amount: "$50,000", rate: "14.75%", status: "Match", color: "#52BFB8" },
                  { abbr: "BNS", amount: "$45,000", rate: "6.0%", status: "Auto Only", color: "#fbbf24" },
                  { abbr: "CIBC", amount: "$55,000", rate: "14.0%", status: "Review", color: "#a78bfa" },
                ].map((b, i) => (
                  <div
                    key={i}
                    data-dashboard-row
                    className="flex items-center justify-between rounded-xl bg-white/5 hover:bg-white/8 border border-white/5 px-3 sm:px-4 py-3 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#52BFB8]/20 to-[#52BFB8]/5 border border-[#52BFB8]/25 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-[#52BFB8] tracking-wider">{b.abbr}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-[#667699]">{b.rate} APR</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="text-sm font-mono text-white tabular-nums">{b.amount}</p>
                      <span
                        className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full"
                        style={{ background: `${b.color}22`, color: b.color }}
                      >
                        {b.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* ID Verification status */}
              <div className="rounded-xl bg-gradient-to-r from-emerald-500/10 to-transparent border border-emerald-400/20 p-4 flex items-center gap-3" data-dashboard-row>
                <ShieldCheck className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white">ID Verified · AML Cleared</p>
                  <p className="text-xs text-[#9BA8C4]">Document verification completed</p>
                </div>
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── PARTNER SECTION ───────────── */}
      <section className="relative py-24 px-5 lg:px-10 bg-[#0B1324]/60">
        <div className="aurora w-[600px] h-[600px] -top-40 left-0 bg-[#60a5fa]/10" />
        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            <div data-fade-up>
              <p className="text-xs tracking-[0.3em] uppercase text-[#52BFB8] font-semibold mb-3">
                FOR INSTITUTIONS
              </p>
              <h2 className="font-cabinet text-4xl md:text-5xl font-bold text-white tracking-tight">
                For Banks, Credit Unions, Brokers & Auto Dealers.
              </h2>
              <p className="mt-6 text-lg text-[#9BA8C4]">
                White-label our pre-qualification engine. Get verified, AML-cleared leads delivered to your team in real-time.
              </p>
              <Link
                to="/partners/verify"
                data-testid="partners-section-cta"
                className="btn-aqua mt-8 group"
              >
                Explore Partner Solutions
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {PARTNER_FEATURES.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div
                    key={i}
                    data-fade-up
                    className="glass-card glass-card-hover rounded-2xl p-5 transition-all"
                    data-testid={`partner-feature-${i}`}
                  >
                    <Icon className="w-7 h-7 text-[#52BFB8] mb-3" />
                    <h4 className="font-cabinet text-base font-semibold text-white mb-1">{p.title}</h4>
                    <p className="text-xs text-[#9BA8C4] leading-relaxed">{p.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── TRUST BADGES MARQUEE ───────────── */}
      <section className="relative py-16 px-5 lg:px-10 overflow-hidden border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-xs tracking-[0.3em] uppercase text-[#667699] mb-8">
            Built on a foundation of
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {TRUST_BADGES.map((b, i) => {
              const Icon = b.icon;
              return (
                <div
                  key={i}
                  data-fade-up
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 backdrop-blur-md hover:border-[#52BFB8]/40 transition-colors"
                  data-testid={`trust-badge-${i}`}
                >
                  <Icon className="w-4 h-4 text-[#52BFB8]" />
                  <span className="text-sm font-medium text-white">{b.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────────── FINAL CTA ───────────── */}
      <section className="relative py-32 px-5 lg:px-10 overflow-hidden">
        <div className="aurora w-[800px] h-[600px] top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-[#52BFB8]/20" />
        <div className="relative max-w-4xl mx-auto text-center" data-fade-up>
          <h2 className="font-cabinet text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-tighter leading-[1.05]">
            Know what you qualify for
            <br />
            <span className="bg-gradient-to-r from-[#52BFB8] via-[#6BD2CB] to-[#3E9690] bg-clip-text text-transparent">
              before you apply.
            </span>
          </h2>
          <p className="mt-6 text-lg text-[#9BA8C4]">
            No credit bureau pull. No paperwork. Just clarity in minutes.
          </p>
          <Link to="/qualify" data-testid="final-cta" className="btn-aqua mt-10 group inline-flex">
            Get Started Today
            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* ───────────── FOOTER ───────────── */}
      <footer className="relative pt-16 pb-8 px-5 lg:px-10 border-t border-white/5 bg-[#040914]">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-10 mb-12">
          <div className="md:col-span-2">
            <img
              src={FINANCIFY_LOGO}
              alt="Financify"
              className="h-12 w-auto mb-5"
              style={{ filter: "brightness(0) invert(1)" }}
            />
            <p className="text-sm text-[#667699] max-w-md leading-relaxed">
              Financify is a Caribbean pre-qualification platform helping you understand your loan eligibility before you apply. We are not a lender.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
            <ul className="space-y-2.5 text-sm text-[#667699]">
              <li><Link to="/qualify" className="hover:text-[#52BFB8]">Pre-Qualification</Link></li>
              <li><Link to="/how-it-works" className="hover:text-[#52BFB8]">How It Works</Link></li>
              <li><Link to="/partners/verify" className="hover:text-[#52BFB8]">For Partners</Link></li>
              <li><Link to="/blog" className="hover:text-[#52BFB8]">Blog</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Company</h4>
            <ul className="space-y-2.5 text-sm text-[#667699]">
              <li><Link to="/about" className="hover:text-[#52BFB8]">About</Link></li>
              <li><Link to="/faq" className="hover:text-[#52BFB8]">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-[#52BFB8]">Contact</Link></li>
              <li><Link to="/privacy" className="hover:text-[#52BFB8]">Privacy</Link></li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-[#667699]">
          <p>&copy; 2026 Financify Caribbean. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Nassau, Bahamas</span>
            <a href="mailto:support@financify-caribbean.com" className="flex items-center gap-1.5 hover:text-[#52BFB8]">
              <Mail className="w-3.5 h-3.5" /> support@financify-caribbean.com
            </a>
          </div>
        </div>
      </footer>

      {/* Finny Chat Widget */}
      <FinnyChat />
    </div>
  );
}

// — Helper components —
function MessageCircleIcon() {
  return (
    <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

function JourneyVisual({ step }) {
  const visuals = [
    // Step 1 — income & debts mockup
    <div key="1" className="glass-card rounded-2xl p-6 w-full">
      <p className="text-xs uppercase tracking-wider text-[#667699] mb-4">Step 1 · Your Profile</p>
      <div className="space-y-3">
        {[
          { label: "Monthly Income", value: "$5,400" },
          { label: "Existing Debts", value: "$680" },
          { label: "Employment", value: "Government · 4y" },
          { label: "Citizenship", value: "Bahamian Citizen" },
        ].map((row, i) => (
          <div key={i} className="flex justify-between items-center pb-3 border-b border-white/5">
            <span className="text-sm text-[#9BA8C4]">{row.label}</span>
            <span className="text-sm text-white font-mono">{row.value}</span>
          </div>
        ))}
      </div>
    </div>,
    // Step 2 — TDSR
    <div key="2" className="glass-card rounded-2xl p-6 w-full text-center">
      <p className="text-xs uppercase tracking-wider text-[#667699] mb-3">Step 2 · TDSR Calculation</p>
      <div className="relative w-48 h-48 mx-auto my-6">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r="40" stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" />
          <circle cx="50" cy="50" r="40" stroke="#52BFB8" strokeWidth="8" fill="none" strokeLinecap="round" strokeDasharray="251" strokeDashoffset="155" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="font-cabinet text-4xl font-bold text-white">38.2%</p>
          <p className="text-xs text-emerald-400">Healthy</p>
        </div>
      </div>
      <p className="text-sm text-[#9BA8C4]">Total Debt-Service Ratio</p>
    </div>,
    // Step 3 — Lender match
    <div key="3" className="glass-card rounded-2xl p-6 w-full">
      <p className="text-xs uppercase tracking-wider text-[#667699] mb-4">Step 3 · 5 Lenders Matched</p>
      <div className="space-y-2">
        {[
          { abbr: "FB", rate: "14.5%" },
          { abbr: "CB", rate: "12.0%" },
          { abbr: "RBC", rate: "14.75%" },
          { abbr: "BNS", rate: "6.0%" },
          { abbr: "CIBC", rate: "14.0%" },
        ].map((b, i) => (
          <div key={i} className="flex items-center justify-between rounded-xl bg-white/5 px-3 py-2 border border-white/5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-[#52BFB8]/15 border border-[#52BFB8]/30 flex items-center justify-center flex-shrink-0">
                <span className="text-[11px] font-bold text-[#52BFB8]">{b.abbr}</span>
              </div>
              <span className="text-xs text-[#9BA8C4]">{b.rate} APR</span>
            </div>
            <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>,
    // Step 4 — ID Verification
    <div key="4" className="glass-card rounded-2xl p-6 w-full text-center">
      <p className="text-xs uppercase tracking-wider text-[#667699] mb-6">Step 4 · ID Verification</p>
      <div className="w-24 h-24 mx-auto mb-5 rounded-full bg-gradient-to-br from-emerald-400 to-[#52BFB8] flex items-center justify-center shadow-[0_0_40px_rgba(82,191,184,0.5)]">
        <ShieldCheck className="w-12 h-12 text-white" />
      </div>
      <p className="font-cabinet text-2xl font-semibold text-white mb-2">Identity Verified</p>
      <p className="text-sm text-[#9BA8C4]">ID extracted · Document clear · AML cleared</p>
    </div>,
    // Step 5 — Submit
    <div key="5" className="glass-card rounded-2xl p-6 w-full text-center">
      <p className="text-xs uppercase tracking-wider text-[#667699] mb-6">Step 5 · Submitted</p>
      <div className="w-24 h-24 mx-auto mb-5 rounded-full bg-gradient-to-br from-[#f472b6] to-[#a78bfa] flex items-center justify-center shadow-[0_0_40px_rgba(244,114,182,0.5)]">
        <Send className="w-12 h-12 text-white" />
      </div>
      <p className="font-cabinet text-2xl font-semibold text-white mb-2">Sent to FB</p>
      <p className="text-sm text-[#9BA8C4]">Your verified profile is on its way to the lender of your choice.</p>
    </div>,
  ];
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <div key={step} className="w-full max-w-md animate-[fadeIn_0.5s_ease-out]">
        {visuals[step]}
      </div>
    </div>
  );
}
